import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ClientApiError } from "../auth/api";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  Toast,
} from "../components/ui";
import {
  cancelClaim,
  cancelSensitiveEdit,
  cancelTransfer,
  createClaim,
  createDelegation,
  createSensitiveEdit,
  createTransfer,
  finalizeTransfer,
  getClaimCompare,
  getEditCompare,
  getEvidenceAccess,
  getModerationQueue,
  getOwnershipDashboard,
  getOwnershipInbox,
  respondDelegation,
  respondTransfer,
  reviewClaim,
  reviewSensitiveEdit,
  revokeDelegation,
  uploadEvidence,
  type AuthorityScope,
  type ClaimItem,
  type DelegationItem,
  type ModerationQueue,
  type OwnershipDashboard,
  type OwnershipInbox,
  type SensitiveEditItem,
  type TransferItem,
} from "../ownership/api";

const dateFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeZone: "Asia/Ho_Chi_Minh",
});
const dateTimeFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Ho_Chi_Minh",
});

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function inDays(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatDate(value: string | null | undefined, includeTime = false): string {
  if (!value) return "Chưa cập nhật";
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;
  return includeTime ? dateTimeFormatter.format(date) : dateFormatter.format(date);
}

function apiProblem(caught: unknown): {
  message: string;
  requestId?: string;
  fields?: Record<string, string>;
} {
  if (caught instanceof ClientApiError) {
    return {
      message: caught.problem.message,
      requestId: caught.problem.requestId,
      fields: caught.problem.fieldErrors,
    };
  }
  return { message: "Không thể xử lý yêu cầu. Vui lòng thử lại." };
}

function statusTone(status: string): "neutral" | "success" | "warning" | "danger" | "info" {
  if (["approved", "completed", "active"].includes(status)) return "success";
  if (["rejected", "revoked", "cancelled", "expired"].includes(status)) return "danger";
  if (["pending", "pending_recipient", "accepted", "invited", "completing"].includes(status)) return "warning";
  if (status === "disputed") return "info";
  return "neutral";
}

function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    pending: "Chờ duyệt",
    approved: "Đã duyệt",
    rejected: "Từ chối",
    cancelled: "Đã hủy",
    disputed: "Tranh chấp",
    pending_recipient: "Chờ người nhận",
    accepted: "Đã chấp nhận",
    completing: "Đang hoàn tất",
    completed: "Hoàn tất",
    expired: "Hết hạn",
    invited: "Đã mời",
    active: "Đang hiệu lực",
    revoked: "Đã thu hồi",
  };
  return <Badge tone={statusTone(value)}>{labels[value] ?? value}</Badge>;
}

function WorkflowCard({
  title,
  status,
  meta,
  children,
}: {
  title: string;
  status: string;
  meta: string;
  children?: React.ReactNode;
}) {
  return (
    <article className="authority-card">
      <header>
        <div>
          <h3>{title}</h3>
          <p>{meta}</p>
        </div>
        <Status value={status} />
      </header>
      {children}
    </article>
  );
}

export function MemberOwnershipInboxPage() {
  const { principal } = useAuth();
  const [data, setData] = useState<OwnershipInbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ReturnType<typeof apiProblem> | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOwnershipInbox());
      setError(null);
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function act(id: string, callback: () => Promise<unknown>, message: string) {
    setBusy(id);
    try {
      await callback();
      setToast({ title: "Đã cập nhật", message });
      await refresh();
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setBusy(null);
    }
  }

  const count = data
    ? data.claims.length + data.transfers.length + data.delegations.length + data.editRequests.length
    : 0;

  return (
    <section className="ownership-page" data-testid="ownership-inbox-page">
      <header className="page-heading authority-heading">
        <div>
          <p className="eyebrow">Ownership & Versioning</p>
          <h2>Quyền và lời mời của tôi</h2>
          <p>Phản hồi transfer/editor, theo dõi claim và yêu cầu chỉnh sửa có bằng chứng.</p>
        </div>
        <Link className="ui-button ui-button--secondary ui-button--md" to="/member/dogs">
          Mở chó của tôi
        </Link>
      </header>

      {loading ? <Skeleton lines={8} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không tải được ownership"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Tải lại", onClick: () => void refresh() }}
        />
      ) : null}
      {!loading && !error && count === 0 ? (
        <EmptyState
          title="Chưa có yêu cầu hoặc lời mời"
          description="Claim, transfer, editor invitation và edit request sẽ xuất hiện tại đây."
        />
      ) : null}

      {!loading && data ? (
        <div className="authority-inbox-grid">
          <section className="authority-panel">
            <header><h3>Transfer ownership</h3><Badge>{data.transfers.length}</Badge></header>
            <div className="authority-stack">
              {data.transfers.map((transfer) => (
                <WorkflowCard
                  key={transfer.id}
                  title={transfer.dogName}
                  status={transfer.status}
                  meta={`${transfer.fromOwnerDisplayName} → ${transfer.toDisplayName} · hiệu lực ${formatDate(transfer.effectiveAt)}`}
                >
                  <p>{transfer.note ?? "Không có ghi chú."}</p>
                  <div className="button-row">
                    <Link className="text-link" to={`/member/dogs/${transfer.dogId}/ownership`}>Chi tiết</Link>
                    {transfer.toUserId === principal?.userId && transfer.status === "pending_recipient" ? (
                      <>
                        <Button
                          size="sm"
                          disabled={busy === transfer.id}
                          onClick={() => void act(
                            transfer.id,
                            () => respondTransfer(transfer.id, { action: "accept", expectedVersion: transfer.version }),
                            "Bạn đã chấp nhận nhận quyền sở hữu.",
                          )}
                        >Chấp nhận</Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busy === transfer.id}
                          onClick={() => void act(
                            transfer.id,
                            () => respondTransfer(transfer.id, { action: "reject", expectedVersion: transfer.version }),
                            "Bạn đã từ chối transfer.",
                          )}
                        >Từ chối</Button>
                      </>
                    ) : null}
                  </div>
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Editor invitations</h3><Badge>{data.delegations.length}</Badge></header>
            <div className="authority-stack">
              {data.delegations.map((delegation) => (
                <WorkflowCard
                  key={delegation.id}
                  title={delegation.dogName}
                  status={delegation.status}
                  meta={`${delegation.scopes.length} scope · hết hạn ${formatDate(delegation.invitationExpiresAt)}`}
                >
                  <p className="scope-line">{delegation.scopes.join(" · ")}</p>
                  {delegation.delegateUserId === principal?.userId && delegation.status === "invited" ? (
                    <div className="button-row">
                      <Button
                        size="sm"
                        disabled={busy === delegation.id}
                        onClick={() => void act(
                          delegation.id,
                          () => respondDelegation(delegation.id, { action: "accept", expectedVersion: delegation.version }),
                          "Quyền editor đã có hiệu lực.",
                        )}
                      >Nhận quyền</Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy === delegation.id}
                        onClick={() => void act(
                          delegation.id,
                          () => respondDelegation(delegation.id, { action: "reject", expectedVersion: delegation.version }),
                          "Bạn đã từ chối lời mời editor.",
                        )}
                      >Từ chối</Button>
                    </div>
                  ) : null}
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Claims</h3><Badge>{data.claims.length}</Badge></header>
            <div className="authority-stack">
              {data.claims.map((claim) => (
                <WorkflowCard
                  key={claim.id}
                  title={claim.dogName}
                  status={claim.status}
                  meta={`${claim.relationType} · gửi ${formatDate(claim.createdAt, true)}`}
                >
                  <p>{claim.reviewerNote ?? claim.explanation}</p>
                  <Link className="text-link" to={`/member/dogs/${claim.dogId}/ownership`}>Mở hồ sơ quyền</Link>
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Edit requests</h3><Badge>{data.editRequests.length}</Badge></header>
            <div className="authority-stack">
              {data.editRequests.map((edit) => (
                <WorkflowCard
                  key={edit.id}
                  title={edit.dogName}
                  status={edit.status}
                  meta={`${edit.criticalFields.join(", ")} · base v${edit.baseVersion}`}
                >
                  <p>{edit.reviewerNote ?? edit.sourceSummary}</p>
                  <Link className="text-link" to={`/member/dogs/${edit.dogId}/ownership`}>Xem version history</Link>
                </WorkflowCard>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      {toast ? <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} /> : null}
    </section>
  );
}

const EDITOR_PRESETS: Record<string, AuthorityScope[]> = {
  basic: ["profile:read", "profile:update_safe", "media:manage"],
  evidence: ["profile:read", "profile:update_safe", "profile:request_sensitive", "media:manage"],
};

function sensitivePatch(field: string, value: string): Record<string, unknown> {
  if (field === "healthRecords") {
    const [testCode, resultCode, testedAt] = value.split("|").map((part) => part.trim());
    return { healthRecords: [{ testCode, resultCode: resultCode || null, testedAt }] };
  }
  if (field === "titleRecords") {
    const [titleDefinitionId, awardedAt, issuingOrganization] = value.split("|").map((part) => part.trim());
    return {
      titleRecords: [{
        titleDefinitionId,
        awardedAt: awardedAt || null,
        issuingOrganization: issuingOrganization || null,
      }],
    };
  }
  return { [field]: value || null };
}

export function DogOwnershipPage() {
  const { id = "" } = useParams();
  const { principal } = useAuth();
  const [dashboard, setDashboard] = useState<OwnershipDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof apiProblem> | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);
  const [tab, setTab] = useState("overview");

  const [claimType, setClaimType] = useState("co_owner");
  const [claimExplanation, setClaimExplanation] = useState("");
  const [claimStart, setClaimStart] = useState(today());
  const [claimEnd, setClaimEnd] = useState("");
  const [claimFile, setClaimFile] = useState<File | null>(null);

  const [recipientSlug, setRecipientSlug] = useState("");
  const [transferEffective, setTransferEffective] = useState(today());
  const [transferExpiry, setTransferExpiry] = useState(inDays(7));
  const [transferNote, setTransferNote] = useState("");
  const [transferFile, setTransferFile] = useState<File | null>(null);

  const [delegateSlug, setDelegateSlug] = useState("");
  const [delegatePreset, setDelegatePreset] = useState("evidence");
  const [delegateExpiry, setDelegateExpiry] = useState(inDays(7));
  const [delegateNote, setDelegateNote] = useState("");

  const [editField, setEditField] = useState("registrationNumber");
  const [editValue, setEditValue] = useState("");
  const [sourceSummary, setSourceSummary] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [editFile, setEditFile] = useState<File | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getOwnershipDashboard(id);
      setDashboard(response.dashboard);
      setError(null);
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function execute(title: string, callback: () => Promise<unknown>) {
    setBusy(true);
    try {
      await callback();
      setToast({ title: "Thành công", message: title });
      setError(null);
      await refresh();
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitClaim(event: React.FormEvent) {
    event.preventDefault();
    if (!dashboard || !claimFile) {
      setError({ message: "Claim bắt buộc phải có document evidence." });
      return;
    }
    await execute("Claim đã được gửi vào hàng đợi reviewer.", async () => {
      const { evidence } = await uploadEvidence(id, "claim", claimFile);
      await createClaim(id, {
        relationType: claimType,
        explanation: claimExplanation,
        evidenceId: evidence.id,
        expectedDogVersion: dashboard.dog.version,
        relationshipStartsAt: claimStart,
        relationshipEndsAt: claimEnd || null,
      });
      setClaimExplanation("");
      setClaimFile(null);
    });
  }

  async function submitTransfer(event: React.FormEvent) {
    event.preventDefault();
    if (!dashboard) return;
    await execute("Lời mời transfer đã gửi cho người nhận.", async () => {
      const evidenceId = transferFile
        ? (await uploadEvidence(id, "transfer", transferFile)).evidence.id
        : undefined;
      await createTransfer(id, {
        recipientSlug,
        effectiveAt: transferEffective,
        invitationExpiresAt: transferExpiry,
        note: transferNote,
        expectedDogVersion: dashboard.dog.version,
        evidenceId,
      });
      setRecipientSlug("");
      setTransferNote("");
      setTransferFile(null);
    });
  }

  async function submitDelegation(event: React.FormEvent) {
    event.preventDefault();
    await execute("Lời mời editor đã được gửi.", async () => {
      await createDelegation(id, {
        delegateSlug,
        scopes: EDITOR_PRESETS[delegatePreset] ?? EDITOR_PRESETS.basic,
        startsAt: today(),
        invitationExpiresAt: delegateExpiry,
        note: delegateNote,
      });
      setDelegateSlug("");
      setDelegateNote("");
    });
  }

  async function submitEdit(event: React.FormEvent) {
    event.preventDefault();
    if (!dashboard) return;
    await execute("Edit request đã được gửi; dữ liệu published chưa thay đổi.", async () => {
      const evidenceId = editFile
        ? (await uploadEvidence(id, "edit_request", editFile)).evidence.id
        : undefined;
      await createSensitiveEdit(id, {
        patch: sensitivePatch(editField, editValue),
        sourceSummary,
        sourceUrl: sourceUrl || undefined,
        evidenceId,
        expectedDogVersion: dashboard.dog.version,
      });
      setEditValue("");
      setSourceSummary("");
      setSourceUrl("");
      setEditFile(null);
    });
  }

  if (loading) return <section className="ownership-page"><Skeleton lines={12} /></section>;
  if (error && !dashboard) {
    return (
      <section className="ownership-page">
        <ErrorState
          title="Không mở được quản lý ownership"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Thử lại", onClick: () => void refresh() }}
        />
      </section>
    );
  }
  if (!dashboard) return null;

  const tabs = [
    { id: "overview", label: "Quyền hiện tại" },
    { id: "actions", label: "Tạo yêu cầu" },
    { id: "history", label: "Lịch sử bất biến" },
  ];

  return (
    <section className="ownership-page" data-testid="dog-ownership-page">
      <header className="page-heading authority-heading">
        <div>
          <p className="eyebrow">Ownership authority</p>
          <h2>{dashboard.dog.registeredName}</h2>
          <p>Version {dashboard.dog.version} · {dashboard.dog.workflowStatus}</p>
        </div>
        <div className="button-row">
          <Link className="ui-button ui-button--secondary ui-button--sm" to={`/dogs/${dashboard.dog.slug}`}>Hồ sơ công khai</Link>
          <Link className="ui-button ui-button--ghost ui-button--sm" to={`/member/dogs/${dashboard.dog.id}/edit`}>Mở hồ sơ</Link>
        </div>
      </header>

      <div className="authority-summary">
        <div><span>Quan hệ của bạn</span><strong>{dashboard.viewer.relationType ?? "Chưa có"}</strong></div>
        <div><span>Scope hiệu lực</span><strong>{dashboard.viewer.scopes.length}</strong></div>
        <div><span>Assignments</span><strong>{dashboard.assignments.filter((item) => item.active).length}</strong></div>
        <div><span>Pending evidence</span><strong>{dashboard.editRequests.filter((item) => item.status === "pending").length + dashboard.claims.filter((item) => item.status === "pending").length}</strong></div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {error ? (
        <ErrorState
          title="Yêu cầu chưa được xử lý"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Đóng lỗi", onClick: () => setError(null) }}
        />
      ) : null}

      {tab === "overview" ? (
        <div className="authority-grid">
          <section className="authority-panel authority-panel--wide">
            <header><h3>Quan hệ và scope</h3><Badge>{dashboard.assignments.length}</Badge></header>
            <div className="authority-assignment-list">
              {dashboard.assignments.map((assignment) => (
                <article key={`${assignment.sourceType}-${assignment.id}`}>
                  <div>
                    <h4>{assignment.displayName}</h4>
                    <p>{assignment.relationType} · từ {formatDate(assignment.startsAt)}</p>
                  </div>
                  <div>
                    <Badge tone={assignment.active ? "success" : "neutral"}>{assignment.active ? "Hiện hành" : "Lịch sử"}</Badge>
                    <p className="scope-line">{assignment.scopes.join(" · ")}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Claims</h3><Badge>{dashboard.claims.length}</Badge></header>
            <div className="authority-stack">
              {dashboard.claims.map((claim) => (
                <WorkflowCard key={claim.id} title={claim.claimantDisplayName} status={claim.status} meta={claim.relationType}>
                  <p>{claim.reviewerNote ?? claim.explanation}</p>
                  {claim.claimantUserId === principal?.userId && ["pending", "disputed"].includes(claim.status) ? (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => void execute("Claim đã được hủy.", () => cancelClaim(claim.id))}
                    >Hủy claim</Button>
                  ) : null}
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Transfers</h3><Badge>{dashboard.transfers.length}</Badge></header>
            <div className="authority-stack">
              {dashboard.transfers.map((transfer) => (
                <WorkflowCard key={transfer.id} title={`${transfer.fromOwnerDisplayName} → ${transfer.toDisplayName}`} status={transfer.status} meta={`v${transfer.version} · hiệu lực ${formatDate(transfer.effectiveAt)}`}>
                  <div className="button-row">
                    {dashboard.viewer.canTransfer && transfer.status === "accepted" ? (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => void execute(
                          "Transfer đã hoàn tất; owner cũ chuyển vào lịch sử.",
                          () => finalizeTransfer(transfer.id, {
                            expectedTransferVersion: transfer.version,
                            expectedDogVersion: dashboard.dog.version,
                          }),
                        )}
                      >Hoàn tất transfer</Button>
                    ) : null}
                    {transfer.fromOwnerUserId === principal?.userId && ["pending_recipient", "accepted"].includes(transfer.status) ? (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => void execute(
                          "Transfer đã được hủy.",
                          () => cancelTransfer(transfer.id, { expectedVersion: transfer.version }),
                        )}
                      >Hủy</Button>
                    ) : null}
                  </div>
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel">
            <header><h3>Delegated editors</h3><Badge>{dashboard.delegations.length}</Badge></header>
            <div className="authority-stack">
              {dashboard.delegations.map((delegation) => (
                <WorkflowCard key={delegation.id} title={delegation.delegateDisplayName} status={delegation.status} meta={delegation.scopes.join(" · ")}>
                  {dashboard.viewer.canDelegate && ["active", "invited"].includes(delegation.status) ? (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => void execute(
                        "Quyền editor đã được thu hồi.",
                        () => revokeDelegation(delegation.id, { expectedVersion: delegation.version }),
                      )}
                    >Thu hồi quyền</Button>
                  ) : null}
                </WorkflowCard>
              ))}
            </div>
          </section>

          <section className="authority-panel authority-panel--wide">
            <header><h3>Sensitive edit requests</h3><Badge>{dashboard.editRequests.length}</Badge></header>
            <div className="authority-stack">
              {dashboard.editRequests.map((edit) => (
                <WorkflowCard key={edit.id} title={edit.criticalFields.join(", ")} status={edit.status} meta={`base v${edit.baseVersion} · ${formatDate(edit.createdAt, true)}`}>
                  <p>{edit.reviewerNote ?? edit.sourceSummary}</p>
                  {edit.requestedByUserId === principal?.userId && edit.status === "pending" ? (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => void execute(
                        "Edit request đã được hủy.",
                        () => cancelSensitiveEdit(edit.id),
                      )}
                    >Hủy yêu cầu</Button>
                  ) : null}
                </WorkflowCard>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      {tab === "actions" ? (
        <div className="authority-form-grid">
          {dashboard.viewer.canClaim ? (
            <form className="authority-form" onSubmit={(event) => void submitClaim(event)}>
              <header><p className="eyebrow">Evidence required</p><h3>Claim quan hệ</h3></header>
              <Select label="Loại quan hệ" value={claimType} onChange={(event) => setClaimType(event.target.value)}>
                <option value="owner">Owner</option>
                <option value="co_owner">Co-owner</option>
                <option value="breeder">Breeder</option>
                <option value="handler">Handler</option>
                <option value="editor">Editor</option>
              </Select>
              <Textarea label="Giải thích" required minLength={20} value={claimExplanation} onChange={(event) => setClaimExplanation(event.target.value)} />
              <div className="form-grid form-grid--two">
                <Input label="Bắt đầu" type="date" required value={claimStart} onChange={(event) => setClaimStart(event.target.value)} />
                <Input label="Kết thúc (nếu có)" type="date" value={claimEnd} onChange={(event) => setClaimEnd(event.target.value)} />
              </div>
              <label className="authority-file-field">
                <span>Tài liệu riêng tư *</span>
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" required onChange={(event) => setClaimFile(event.target.files?.[0] ?? null)} />
                <small>PDF/JPG/PNG/WebP, tối đa 8 MB. Không công khai.</small>
              </label>
              <Button type="submit" disabled={busy}>Gửi claim</Button>
            </form>
          ) : null}

          {dashboard.viewer.canTransfer ? (
            <form className="authority-form" onSubmit={(event) => void submitTransfer(event)}>
              <header><p className="eyebrow">Two-party acceptance</p><h3>Chuyển ownership</h3></header>
              <Input label="Username người nhận" required value={recipientSlug} onChange={(event) => setRecipientSlug(event.target.value)} placeholder="username" />
              <div className="form-grid form-grid--two">
                <Input label="Ngày hiệu lực" type="date" required value={transferEffective} onChange={(event) => setTransferEffective(event.target.value)} />
                <Input label="Hết hạn lời mời" type="date" required value={transferExpiry} onChange={(event) => setTransferExpiry(event.target.value)} />
              </div>
              <Textarea label="Ghi chú chuyển quyền" required minLength={10} value={transferNote} onChange={(event) => setTransferNote(event.target.value)} />
              <label className="authority-file-field">
                <span>Document bổ sung (tùy chọn)</span>
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setTransferFile(event.target.files?.[0] ?? null)} />
              </label>
              <Button type="submit" disabled={busy}>Gửi lời mời transfer</Button>
            </form>
          ) : null}

          {dashboard.viewer.canDelegate ? (
            <form className="authority-form" onSubmit={(event) => void submitDelegation(event)}>
              <header><p className="eyebrow">Scoped delegation</p><h3>Mời delegated editor</h3></header>
              <Input label="Username editor" required value={delegateSlug} onChange={(event) => setDelegateSlug(event.target.value)} />
              <Select label="Preset quyền" value={delegatePreset} onChange={(event) => setDelegatePreset(event.target.value)}>
                <option value="basic">Cập nhật an toàn + media</option>
                <option value="evidence">Cập nhật + gửi edit evidence</option>
              </Select>
              <Input label="Hết hạn lời mời" type="date" required value={delegateExpiry} onChange={(event) => setDelegateExpiry(event.target.value)} />
              <Textarea label="Ghi chú" value={delegateNote} onChange={(event) => setDelegateNote(event.target.value)} />
              <Button type="submit" disabled={busy}>Mời editor</Button>
            </form>
          ) : null}

          {dashboard.viewer.canRequestSensitiveEdit ? (
            <form className="authority-form" onSubmit={(event) => void submitEdit(event)}>
              <header><p className="eyebrow">Before / after version</p><h3>Yêu cầu sửa dữ liệu nhạy cảm</h3></header>
              <Select label="Trường cần sửa" value={editField} onChange={(event) => setEditField(event.target.value)}>
                <option value="registeredName">Tên đăng ký</option>
                <option value="dateOfBirth">Ngày sinh</option>
                <option value="registrationNumber">Số đăng ký</option>
                <option value="pedigreeNumber">Số phả hệ</option>
                <option value="microchipNumber">Microchip</option>
                <option value="breederName">Breeder</option>
                <option value="healthRecords">Health: test|result|YYYY-MM-DD</option>
                <option value="titleRecords">Title: definitionId|date|issuer</option>
              </Select>
              <Input
                label="Giá trị đề nghị"
                required
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                hint={editField === "healthRecords" || editField === "titleRecords" ? "Nhập đúng cấu trúc được mô tả." : undefined}
              />
              <Textarea label="Nguồn và lý do" required minLength={20} value={sourceSummary} onChange={(event) => setSourceSummary(event.target.value)} />
              <Input label="URL nguồn HTTPS" type="url" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://..." />
              <label className="authority-file-field">
                <span>Document evidence</span>
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setEditFile(event.target.files?.[0] ?? null)} />
                <small>Cần URL HTTPS hoặc document evidence.</small>
              </label>
              <Button type="submit" disabled={busy}>Gửi edit request</Button>
            </form>
          ) : null}
        </div>
      ) : null}

      {tab === "history" ? (
        <section className="authority-panel authority-panel--wide">
          <header><h3>Authority history</h3><Badge>{dashboard.history.length}</Badge></header>
          {dashboard.history.length === 0 ? (
            <EmptyState title="Chưa có lịch sử" description="Mọi mutation authority sẽ được append tại đây." />
          ) : (
            <ol className="authority-timeline">
              {dashboard.history.map((event) => (
                <li key={event.id}>
                  <span aria-hidden="true" />
                  <div>
                    <div className="authority-timeline__title">
                      <strong>{event.eventType}</strong>
                      <time>{formatDate(event.createdAt, true)}</time>
                    </div>
                    <p>{event.relationType ?? event.sourceType} · {event.scopes.join(" · ") || "không có scope"}</p>
                    <code>{event.sourceId}</code>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      ) : null}

      {toast ? <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} /> : null}
    </section>
  );
}

export function OwnershipModerationPage() {
  const [queue, setQueue] = useState<ModerationQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ReturnType<typeof apiProblem> | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);
  const [compare, setCompare] = useState<{ type: "claim" | "edit"; id: string; value: Record<string, unknown> } | null>(null);
  const [reviewerNote, setReviewerNote] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setQueue(await getModerationQueue());
      setError(null);
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function execute(id: string, message: string, callback: () => Promise<unknown>) {
    setBusy(id);
    try {
      await callback();
      setToast({ title: "Đã xử lý", message });
      setReviewerNote("");
      setCompare(null);
      await refresh();
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setBusy(null);
    }
  }

  async function openClaimCompare(claim: ClaimItem) {
    setBusy(claim.id);
    try {
      const response = await getClaimCompare(claim.id);
      setCompare({ type: "claim", id: claim.id, value: response.compare });
      setError(null);
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setBusy(null);
    }
  }

  async function openEditCompare(edit: SensitiveEditItem) {
    setBusy(edit.id);
    try {
      const response = await getEditCompare(edit.id);
      setCompare({ type: "edit", id: edit.id, value: response.compare });
      setError(null);
    } catch (caught) {
      setError(apiProblem(caught));
    } finally {
      setBusy(null);
    }
  }

  async function openEvidence(evidenceId: string) {
    try {
      const access = await getEvidenceAccess(evidenceId);
      window.open(access.url, "_blank", "noopener,noreferrer");
    } catch (caught) {
      setError(apiProblem(caught));
    }
  }

  const total = useMemo(
    () => queue ? queue.claims.length + queue.transfers.length + queue.editRequests.length : 0,
    [queue],
  );

  return (
    <section className="ownership-page ownership-moderation" data-testid="ownership-moderation-page">
      <header className="page-heading authority-heading">
        <div>
          <p className="eyebrow">Moderator evidence queue</p>
          <h2>Ownership & sensitive changes</h2>
          <p>Đối chiếu current state, evidence và version trước khi quyết định.</p>
        </div>
        <Button variant="secondary" onClick={() => void refresh()}>Làm mới</Button>
      </header>

      <div className="authority-summary">
        <div><span>Claims</span><strong>{queue?.claims.length ?? 0}</strong></div>
        <div><span>Transfers</span><strong>{queue?.transfers.length ?? 0}</strong></div>
        <div><span>Edit requests</span><strong>{queue?.editRequests.length ?? 0}</strong></div>
        <div><span>Tổng chờ xử lý</span><strong>{total}</strong></div>
      </div>

      {loading ? <Skeleton lines={10} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không xử lý được hàng đợi"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Đóng lỗi", onClick: () => setError(null) }}
        />
      ) : null}
      {!loading && queue && total === 0 ? (
        <EmptyState title="Hàng đợi trống" description="Không có claim, transfer hoặc edit request cần xử lý." />
      ) : null}

      {queue && total > 0 ? (
        <div className="moderation-layout">
          <div className="authority-stack">
            {queue.claims.map((claim) => (
              <WorkflowCard key={claim.id} title={`Claim ${claim.relationType}: ${claim.dogName}`} status={claim.status} meta={`${claim.claimantDisplayName} · base v${claim.baseDogVersion}`}>
                <p>{claim.explanation}</p>
                <div className="button-row">
                  <Button size="sm" variant="secondary" disabled={busy === claim.id} onClick={() => void openClaimCompare(claim)}>So sánh & evidence</Button>
                  <Button size="sm" variant="ghost" onClick={() => void openEvidence(claim.evidenceId)}>Mở document</Button>
                </div>
              </WorkflowCard>
            ))}
            {queue.transfers.map((transfer) => (
              <WorkflowCard key={transfer.id} title={`Transfer: ${transfer.dogName}`} status={transfer.status} meta={`${transfer.fromOwnerDisplayName} → ${transfer.toDisplayName} · v${transfer.version}`}>
                <p>Hiệu lực {formatDate(transfer.effectiveAt)} · hết hạn {formatDate(transfer.invitationExpiresAt)}</p>
                {transfer.status === "accepted" ? (
                  <Button
                    size="sm"
                    disabled={busy === transfer.id}
                    onClick={() => void execute(
                      transfer.id,
                      "Transfer đã được moderator fallback hoàn tất.",
                      () => finalizeTransfer(transfer.id, {
                        expectedTransferVersion: transfer.version,
                        expectedDogVersion: transfer.baseDogVersion,
                        moderatorNote: reviewerNote || undefined,
                      }),
                    )}
                  >Finalize transfer</Button>
                ) : null}
              </WorkflowCard>
            ))}
            {queue.editRequests.map((edit) => (
              <WorkflowCard key={edit.id} title={`Edit: ${edit.dogName}`} status={edit.status} meta={`${edit.criticalFields.join(", ")} · base v${edit.baseVersion}`}>
                <p>{edit.sourceSummary}</p>
                <Button size="sm" variant="secondary" disabled={busy === edit.id} onClick={() => void openEditCompare(edit)}>So sánh before/after</Button>
              </WorkflowCard>
            ))}
          </div>

          <aside className="compare-panel">
            <header>
              <p className="eyebrow">Compare view</p>
              <h3>{compare ? (compare.type === "claim" ? "Ownership claim" : "Sensitive edit") : "Chọn một yêu cầu"}</h3>
            </header>
            {compare ? (
              <>
                <pre>{JSON.stringify(compare.value, null, 2)}</pre>
                <Textarea label="Reviewer note" value={reviewerNote} onChange={(event) => setReviewerNote(event.target.value)} placeholder="Ghi rõ căn cứ quyết định" />
                {compare.type === "claim" ? (
                  <div className="button-row">
                    <Button disabled={busy === compare.id} onClick={() => void execute(
                      compare.id,
                      "Claim đã được duyệt.",
                      () => reviewClaim(compare.id, {
                        action: "approve",
                        reviewerNote: reviewerNote || undefined,
                        expectedDogVersion: Number(compare.value.currentDogVersion),
                      }),
                    )}>Duyệt claim</Button>
                    <Button variant="danger" disabled={busy === compare.id} onClick={() => void execute(
                      compare.id,
                      "Claim đã bị từ chối.",
                      () => reviewClaim(compare.id, {
                        action: "reject",
                        reviewerNote,
                        expectedDogVersion: Number(compare.value.currentDogVersion),
                      }),
                    )}>Từ chối</Button>
                    <Button variant="secondary" disabled={busy === compare.id} onClick={() => void execute(
                      compare.id,
                      "Hồ sơ đã chuyển sang disputed.",
                      () => reviewClaim(compare.id, {
                        action: "dispute",
                        reviewerNote,
                        expectedDogVersion: Number(compare.value.currentDogVersion),
                      }),
                    )}>Mở tranh chấp</Button>
                  </div>
                ) : (
                  <div className="button-row">
                    <Button disabled={busy === compare.id} onClick={() => void execute(
                      compare.id,
                      "Edit request đã được áp dụng.",
                      () => reviewSensitiveEdit(compare.id, {
                        action: "approve",
                        reviewerNote: reviewerNote || undefined,
                        expectedDogVersion: Number(compare.value.currentDogVersion),
                      }),
                    )}>Duyệt thay đổi</Button>
                    <Button variant="danger" disabled={busy === compare.id} onClick={() => void execute(
                      compare.id,
                      "Edit request đã bị từ chối.",
                      () => reviewSensitiveEdit(compare.id, {
                        action: "reject",
                        reviewerNote,
                        expectedDogVersion: Number(compare.value.currentDogVersion),
                      }),
                    )}>Từ chối</Button>
                  </div>
                )}
              </>
            ) : (
              <p>Chọn “So sánh” để tải current state, before/after và metadata evidence từ backend.</p>
            )}
          </aside>
        </div>
      ) : null}

      {toast ? <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} /> : null}
    </section>
  );
}
